import {
  Delete,
  Download,
  Edit,
  TextStyleSmall,
} from '@wix/wix-ui-icons-common/on-stage';
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import {
  RadioGroup as RadioGroupRAC,
  Radio as RadioRAC,
  Button as ButtonRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';
import SignaturePad, {
  type Options as SignaturePadOptions,
} from 'signature_pad';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { type WixFile } from '../form/types';

import { type SignatureProps } from './signature-field-props';
import {
  SignatureMode,
  SignatureView,
  SignatureFormat,
} from './signature-types';
import { useSignatureField } from './use-signature-field';
import { resizeCanvas } from './utils/canvas';
import {
  decodeDataUrl,
  dataUrlToWixFile,
  encodeDataUrl,
} from './utils/data-url';
import { isExternalImage } from './utils/drawer';
import { decodePoints, encodePoints } from './utils/points';

export type SignatureComponentProps = {
  children: ReactNode;
};

export const Signature = ({ children }: SignatureComponentProps) => {
  const { id, FieldLayout } = useFieldPropsV2<SignatureProps>();

  const labelElement = findChildOfType(children, Signature.Label);
  const inputElement = findChildOfType(children, Signature.Input);
  const descriptionElement = findChildOfType(children, Signature.Description);
  const errorElement = findChildOfType(children, Signature.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => inputElement}
      renderDescription={() => (
        <>
          {errorElement}
          {descriptionElement}
        </>
      )}
    />
  );
};

type InputProps = {
  children: ReactNode;
};

type InputInternalProps = {
  signatureMode: SignatureMode;
  setSignatureMode: (mode: SignatureMode) => void;
  handleClear: () => void;
  clearable: boolean;
  signatureView: SignatureView;
  signatureValueForView?: WixFile;
  handleChange: (value: WixFile | null) => void;
  handleBeforeChange: () => void;
};

const Input = ({ children }: InputProps) => {
  const { value, onChange, target } = useFieldPropsV2<SignatureProps>();

  const {
    signatureMode,
    setSignatureMode,
    handleClear,
    clearable,
    signatureView,
    signatureValueForView,
    handleChange,
    handleBeforeChange,
  } = useSignatureField({ target, value, onChange });

  const headerElement = findChildOfType(children, Input.Header);
  const valueElement = findChildOfType(children, Input.Value);

  return (
    <>
      {applyInternalProps<InputInternalProps>(headerElement, {
        signatureMode,
        setSignatureMode,
        handleClear,
        clearable,
        signatureView,
        signatureValueForView,
        handleChange,
        handleBeforeChange,
      })}
      {applyInternalProps<InputInternalProps>(valueElement, {
        signatureMode,
        setSignatureMode,
        handleClear,
        clearable,
        signatureView,
        signatureValueForView,
        handleChange,
        handleBeforeChange,
      })}
    </>
  );
};

type HeaderProps = {
  children: ReactNode;
  className?: string;
};

const Header = ({ children, className, ...internal }: HeaderProps) => {
  const { signatureMode, setSignatureMode, clearable, handleClear } =
    internal as InputInternalProps;
  const { uploadEnabled } = useFieldPropsV2<SignatureProps>();
  const { t } = useTranslation();

  const tabsElement = findChildOfType(children, Header.Tabs);
  const clearElement = findChildOfType(children, Header.Clear);

  const modeDescription = useMemo(() => {
    switch (signatureMode) {
      case SignatureMode.DRAW:
        return t('field.signature.mode.draw.description');
      case SignatureMode.TYPE:
        return t('field.signature.mode.type.description');
      case SignatureMode.UPLOAD:
        return t('field.signature.mode.upload.description');
      default:
        return '';
    }
  }, [signatureMode, t]);

  return (
    <div className={className}>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {modeDescription}
      </div>
      {applyInternalProps<HeaderInternalProps>(tabsElement, {
        signatureMode,
        setSignatureMode,
        uploadEnabled,
      })}
      {clearable &&
        applyInternalProps<ClearInternalProps>(clearElement, {
          handleClear,
        })}
    </div>
  );
};

type HeaderInternalProps = {
  signatureMode: SignatureMode;
  setSignatureMode: (mode: SignatureMode) => void;
  uploadEnabled?: boolean;
};

type ClearInternalProps = {
  handleClear: () => void;
};

type TabsProps = {
  children: ReactNode;
  className?: string;
};

const Tabs = ({ children, className, ...internal }: TabsProps) => {
  const { signatureMode, setSignatureMode, uploadEnabled } =
    internal as HeaderInternalProps;
  const { labelId } = useFieldAttributes();

  const handleChange = useCallback(
    (value: string) => {
      setSignatureMode(value as SignatureMode);
    },
    [setSignatureMode],
  );

  const modes = useMemo(() => {
    const allModes = [SignatureMode.DRAW, SignatureMode.TYPE];
    if (uploadEnabled) {
      allModes.push(SignatureMode.UPLOAD);
    }
    return allModes;
  }, [uploadEnabled]);

  const tabElement = findChildOfType(children, Tabs.Tab);

  return (
    <RadioGroupRAC
      value={signatureMode}
      onChange={handleChange}
      aria-labelledby={labelId}
      className={className}
    >
      {modes.map((mode) =>
        applyInternalProps<TabInternalProps>(tabElement, {
          key: mode,
          mode,
        }),
      )}
    </RadioGroupRAC>
  );
};

type TabInternalProps = {
  mode: SignatureMode;
};

type TabProps = {
  children: ReactNode;
  className?: string;
};

const Tab = ({ children, className, ...internal }: TabProps) => {
  const { mode } = internal as TabInternalProps;
  const { t } = useTranslation();

  const iconElement = findChildOfType(children, Tab.Icon);

  const ariaLabel = useMemo(() => {
    switch (mode) {
      case SignatureMode.DRAW:
        return t('field.signature.mode.draw.label');
      case SignatureMode.TYPE:
        return t('field.signature.mode.type.label');
      case SignatureMode.UPLOAD:
        return t('field.signature.mode.upload.label');
      default:
        return '';
    }
  }, [mode, t]);

  return (
    <RadioRAC value={mode} aria-label={ariaLabel} className={className}>
      {applyInternalProps<TabIconInternalProps>(iconElement, {
        mode,
      })}
    </RadioRAC>
  );
};

type TabIconInternalProps = {
  mode: SignatureMode;
};

type TabIconProps = {
  className?: string;
};

const TabIcon = ({ className, ...internal }: TabIconProps) => {
  const { mode } = internal as TabIconInternalProps;

  const IconComponent = useMemo(() => {
    switch (mode) {
      case SignatureMode.DRAW:
        return Edit;
      case SignatureMode.TYPE:
        return TextStyleSmall;
      case SignatureMode.UPLOAD:
        return Download;
      default:
        return null;
    }
  }, [mode]);

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} size="24" aria-hidden="true" />;
};

type ClearProps = {
  className?: string;
};

const Clear = ({ className, ...internal }: ClearProps) => {
  const { handleClear } = internal as ClearInternalProps;
  const { t } = useTranslation();
  const { inputId } = useFieldAttributes();

  return (
    <ButtonRAC
      onPress={handleClear}
      aria-label={t('field.signature.clear-button.label')}
      aria-controls={inputId}
      className={className}
    >
      <Delete size="24" aria-hidden="true" />
    </ButtonRAC>
  );
};

type ValueProps = {
  children: ReactNode;
  className?: string;
};

const Value = ({ children, className, ...internal }: ValueProps) => {
  const {
    signatureView,
    signatureValueForView,
    handleChange,
    handleBeforeChange,
  } = internal as InputInternalProps;
  const { readOnly, onBlur, onFocus, hasError } =
    useFieldPropsV2<SignatureProps>();
  const { t } = useTranslation();

  const canvasElement = findChildOfType(children, Value.Canvas);
  const typeElement = findChildOfType(children, Value.Type);
  const uploadElement = findChildOfType(children, Value.Upload);

  return (
    <div
      className={className}
      role="region"
      aria-label={t('field.signature.input-area') || 'Signature input area'}
      data-invalid={hasError}
    >
      {signatureView === SignatureView.DRAW &&
        applyInternalProps<ValueInternalProps>(canvasElement, {
          value: signatureValueForView,
          onChange: handleChange,
          onBeforeChange: handleBeforeChange,
          readOnly,
          onBlur,
          onFocus,
        })}
      {signatureView === SignatureView.TYPE &&
        applyInternalProps<ValueInternalProps>(typeElement, {
          value: signatureValueForView,
          onChange: handleChange,
          onBeforeChange: handleBeforeChange,
          readOnly,
          onBlur,
          onFocus,
        })}
      {(signatureView === SignatureView.UPLOAD ||
        signatureView === SignatureView.IMAGE) &&
        applyInternalProps<ValueInternalProps>(uploadElement, {
          value: signatureValueForView,
          onChange: handleChange,
          onBeforeChange: handleBeforeChange,
          readOnly,
          onBlur,
          onFocus,
        })}
    </div>
  );
};

type ValueInternalProps = {
  value?: WixFile | null;
  onChange: (value: WixFile | null) => void;
  onBeforeChange?: () => void;
  readOnly?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
};

type CanvasProps = {
  className?: string;
};

const Canvas = ({ className, ...internal }: CanvasProps) => {
  const { value, onChange, onBeforeChange, readOnly, onBlur, onFocus } =
    internal as ValueInternalProps;
  const { id } = useFieldPropsV2<SignatureProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { t } = useTranslation();

  const pad = useRef<SignaturePad>();
  const canvasRef = useRef<HTMLCanvasElement>();
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const valueUrl = value?.url;

  const [ariaLabel, setAriaLabel] = useState(
    t('field.signature.canvas.aria-label.empty'),
  );

  useFocusFieldEvent(() => {
    canvasRef.current?.focus();
  }, id);

  const options = useMemo(
    (): SignaturePadOptions => ({
      throttle: 0,
      dotSize: 2,
      minWidth: 1,
      maxWidth: 3,
      velocityFilterWeight: 0.2,
    }),
    [],
  );

  const getSignature = useCallback((): string => {
    if (pad.current?.isEmpty()) {
      return '';
    }
    const dataPoints = pad.current?.toData();
    return dataPoints
      ? encodePoints(dataPoints)
      : pad.current?.toDataURL() ?? '';
  }, []);

  const handleChange = useCallback(() => {
    const isEmpty = pad.current?.isEmpty();
    setAriaLabel(
      isEmpty
        ? t('field.signature.canvas.aria-label.empty')
        : t('field.signature.canvas.aria-label.signed'),
    );
    onChange(dataUrlToWixFile(getSignature()));
  }, [getSignature, onChange, t]);

  const handleBeforeChangeInternal = useCallback(() => {
    onBeforeChange?.();
  }, [onBeforeChange]);

  const mountCanvas = useCallback(
    (node: HTMLCanvasElement | null) => {
      const isMount = node != null;
      if (isMount) {
        canvasRef.current = node;
        pad.current = new SignaturePad(node, options);
        resizeCanvas(canvasRef.current, pad.current);
        if (valueUrl) {
          const points = decodePoints(valueUrl);
          if (points) {
            pad.current.fromData(points);
            setAriaLabel(t('field.signature.canvas.aria-label.signed'));
          }
        } else {
          pad.current.clear();
          setAriaLabel(t('field.signature.canvas.aria-label.empty'));
        }

        pad.current.addEventListener('beginStroke', handleBeforeChangeInternal);
        pad.current.addEventListener('endStroke', handleChange);

        if (typeof ResizeObserver !== 'undefined' && !resizeObserver.current) {
          resizeObserver.current = new ResizeObserver(() => {
            if (canvasRef.current) {
              resizeCanvas(canvasRef.current, pad?.current);
            }
          });
        }
        resizeObserver.current?.observe(node);
      } else {
        resizeObserver.current?.disconnect();
        pad.current?.removeEventListener('endStroke', handleChange);
        pad.current?.removeEventListener(
          'beginStroke',
          handleBeforeChangeInternal,
        );
      }
    },
    [options, t, valueUrl, handleBeforeChangeInternal, handleChange],
  );

  useEffect(() => {
    if (!pad.current) {
      return;
    }

    if (readOnly) {
      pad.current.off();
    } else {
      pad.current.on();
    }
  }, [readOnly]);

  return (
    <div className={className}>
      <canvas
        ref={mountCanvas}
        id={inputId}
        data-input-id={inputId}
        data-has-value={!!valueUrl}
        style={{ width: '100%', height: '100%' }}
        onFocus={onFocus}
        onBlur={onBlur}
        tabIndex={0}
        role="img"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
      />
    </div>
  );
};

type TypeProps = {
  children: ReactNode;
  className?: string;
};

const Type = ({ children, className, ...internal }: TypeProps) => {
  const { value, onChange, onBeforeChange, readOnly } =
    internal as ValueInternalProps;
  const { id } = useFieldPropsV2<SignatureProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const { t } = useTranslation();

  const decodedValue = value?.url ? decodeDataUrl(value.url) : undefined;
  const { data: signatureText = '' } = decodedValue ?? {
    mimeType: '',
    data: '',
  };

  useFocusFieldEvent(() => {
    const input = document.getElementById(inputId);
    input?.focus({ preventScroll: true });
  }, id);

  useEffect(() => {
    if (!value) {
      const input = document.getElementById(inputId) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
  }, [value, inputId]);

  const handleChange = useCallback(
    (text: string) => {
      onBeforeChange?.();
      if (text.trim()) {
        onChange(
          dataUrlToWixFile(
            encodeDataUrl({
              data: text,
              mimeType: SignatureFormat.TEXT,
            }),
          ),
        );
      } else {
        onChange(null);
      }
    },
    [onBeforeChange, onChange],
  );

  const valueElement = findChildOfType(children, Type.Value);

  return (
    <TextFieldRAC
      value={signatureText}
      onChange={handleChange}
      isReadOnly={readOnly}
      className={className}
    >
      {applyInternalProps<TypeValueInternalProps>(valueElement, {
        inputId,
        ariaDescribedBy,
        placeholder: t('field.signature.text.placeholder'),
      })}
    </TextFieldRAC>
  );
};

type TypeValueInternalProps = {
  inputId: string;
  ariaDescribedBy?: string;
  placeholder?: string;
};

type TypeValueProps = {
  className?: string;
};

const TypeValue = ({ className, ...internal }: TypeValueProps) => {
  const { inputId, ariaDescribedBy, placeholder } =
    internal as TypeValueInternalProps;
  const { label } = useFieldPropsV2<SignatureProps>();

  return (
    <InputRAC
      id={inputId}
      aria-describedby={ariaDescribedBy}
      aria-label={label}
      placeholder={placeholder}
      className={className}
    />
  );
};

type UploadProps = {
  children: ReactNode;
  className?: string;
};

const Upload = ({ children, className, ...internal }: UploadProps) => {
  const { value, onChange, readOnly } = internal as ValueInternalProps;

  const buttonElement = findChildOfType(children, Upload.Button);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) {
        onChange(null);
        return;
      }
      const file = files[0];
      const fileReader = new FileReader();
      fileReader.onload = function () {
        onChange(dataUrlToWixFile(this.result as string, file.name));
      };
      fileReader.readAsDataURL(file);
    },
    [onChange],
  );

  const hasUploadedImage = value && isExternalImage(value);

  const { t } = useTranslation();

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />
      {!hasUploadedImage &&
        applyInternalProps<UploadButtonInternalProps>(buttonElement, {
          fileInputRef,
          readOnly,
        })}
      {hasUploadedImage && value.url && (
        <img
          src={value.url}
          alt={
            t('field.signature.uploaded-image.alt') ||
            'Uploaded signature image'
          }
          style={{ maxWidth: '100%', maxHeight: '200px' }}
        />
      )}
    </div>
  );
};

type UploadButtonInternalProps = {
  fileInputRef: React.RefObject<HTMLInputElement>;
  readOnly?: boolean;
};

type UploadButtonProps = {
  className?: string;
};

const UploadButton = ({ className, ...internal }: UploadButtonProps) => {
  const { fileInputRef, readOnly } = internal as UploadButtonInternalProps;
  const { t } = useTranslation();
  const { inputId, ariaDescribedBy, labelId } = useFieldAttributes();

  const handlePress = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  return (
    <ButtonRAC
      id={inputId}
      onPress={handlePress}
      isDisabled={readOnly}
      aria-labelledby={labelId}
      aria-describedby={ariaDescribedBy}
      className={className}
    >
      {t('field.signature.settings.upload-button.label')}
    </ButtonRAC>
  );
};

Signature.Label = Label;
Signature.Input = Input;
Signature.Description = Description;
Signature.Error = Error;

Input.Header = Header;
Input.Value = Value;

Header.Tabs = Tabs;
Header.Clear = Clear;

Tabs.Tab = Tab;
Tab.Icon = TabIcon;

Value.Canvas = Canvas;
Value.Type = Type;
Value.Upload = Upload;

Type.Value = TypeValue;
Upload.Button = UploadButton;
